import React, { useEffect, useState } from 'react';
import { StatusTip } from '@tencent/tea-component';
import { debounce } from 'lodash-es';
import { VideoPlayProps } from './VideoPlay.type';
import { RemoteStream } from '@tencent/wemeet-websdk-next';
import wemeetClient from '@utils/wemeetClient';
import { captureException } from '@utils/sentry';
import ISSUE_NAME from '@constant/issue-name';
import { isWxAppUser } from '@utils/helper/user';
import { getLoadingMapValue, setLoadingMapValue } from '@utils/helper/media';

import './VideoPlay.scss';
import VideoMouse from '@component/video-mouse/VideoMouse';
import { VideoPrefix } from '@constant/media';

const VideoPlay: React.FC<VideoPlayProps> = (props) => {
  const [loading, setLoading] = useState(false);
  const [layerData, setLayerData] = useState<any>();

  const debouncedLoading = debounce((streamId) => {
    if (getLoadingMapValue(streamId)) {
      setLoading(true);
    }
  }, 3000);

  const onStreamPlayerStateChange = (event: { state: any; type: any; reason: any }) => {
    const { stream } = props;
    const streamType = (stream as RemoteStream).getType();
    const streamId = stream.getId();
    const { state, type, reason } = event;
    if ((state === 'PAUSED' && type === 'video' && streamType === 'main') || streamType === 'auxiliary') {
      if (reason === 'mute') {
        // 设置ui loading
        // 2021.3.26trtcsdk 同事rychouwei建议，屏幕共享如果不是桌面模式，只是选了一个应用，切到后台会采集不到数据，一直触发mute和unmute
        // 建议触发mute之后3s没触发unmute再显示loading
        debouncedLoading(streamId);
        setLoadingMapValue(streamId, true);
      } else if (reason === 'unmute') {
        // 设置ui loading
        setLoading(false);
        setLoadingMapValue(streamId, false);
      }
    }
    if (state === 'PLAYING' && type === 'video') {
      setLoading(false);
      setLoadingMapValue(streamId, false);
    }
  };

  const onLayerChange = (event: { userId: any; tinyId: any; layerData: any }) => {
    setLayerData(event.layerData);
  };

  useEffect(() => {
    const cameraOnAndNoVideo = props.cameraOn && (!props.stream || !props.stream?.hasVideo());
    setLoading(cameraOnAndNoVideo);
  }, [props.stream, props.cameraOn]);

  useEffect(() => {
    const { stream } = props;
    // 远端流状态监听
    if (props.isLocal === false && stream) {
      console.log('进入监听----');
      console.log('stream:', stream);
      stream.on('player-state-changed', onStreamPlayerStateChange);
      // @ts-ignore
      stream.on('layerData', onLayerChange);
    }

    // 组件卸载时取消监听
    return () => {
      if (props.isLocal === false && stream) {
        // @ts-ignore
        (stream as RemoteStream).off('player-state-changed', onStreamPlayerStateChange);
        // @ts-ignore
        (stream as RemoteStream).off('layerData', onLayerChange);
      }
    };
  }, [props.stream]);
  return (
    <div className="video-play">
      {loading && <StatusTip status={'loading'} loadingText="" className="video-loading" />}
      <video
        ref={(el) => {
          if (el) {
            // 非大视频非本地流的转换为小流, 微信用户不切换大小流
            if (props.isSwitchSmall && props.stream && !isWxAppUser(props.userId)) {
              console.log(
                `切换小流 userId: ${props.stream.getUserId()} streamId: ${props.stream.getId()} videoId: ${
                  props.videoId
                }`,
              );
              try {
                wemeetClient.mediaCtrl.setRemoteVideoStreamType(props.stream as RemoteStream, 'small');
              } catch (error) {
                console.log('切换小流错误:', error);

                captureException(ISSUE_NAME.SWITCH_SMALL_STREAM_ERROR, 'VideoPlay', {
                  error,
                });
              }
            }

            // 微信用户不切换大小流
            if (props.isSwitchBig && props.stream && !isWxAppUser(props.userId)) {
              console.log(
                `切换大流 userId: ${props.stream.getUserId()} streamId: ${props.stream.getId()} videoId: ${
                  props.videoId
                }`,
              );
              try {
                wemeetClient.mediaCtrl.setRemoteVideoStreamType(props.stream as RemoteStream, 'big');
              } catch (error) {
                console.log('切换大流错误:', error);

                captureException(ISSUE_NAME.SWITCH_BIG_STREAM_ERROR, 'VideoPlay', {
                  error,
                });
              }
            }

            // 流一致就不赋值新的，防止闪屏
            // @ts-ignore
            if (el?.srcObject === props.stream?.mediaStream_) {
              return;
            }
            // eslint-disable-next-line no-param-reassign
            el.id = `${props.prefix}-${props?.videoId}`;
            // @ts-ignore
            // eslint-disable-next-line no-param-reassign
            el.srcObject = props.stream?.mediaStream_;
          }
        }}
        autoPlay
        muted
        playsInline
        className={props.className}
        onContextMenu={() => {}}
        // 旧版本加的，作用是什么还不清楚
        // @ts-ignore
        type={props.streamType}
      />
      {/* 右侧的160*90的视频不需要展示鼠标，展示了鼠标图像会被压缩到1px完全看不到，不展示了 */}
      {props.prefix === VideoPrefix.big ? (
        <VideoMouse videoId={`${props.prefix}-${props?.videoId}`} layerData={layerData} id={props?.videoId} />
      ) : null}
    </div>
  );
};

export default React.memo(VideoPlay);
